import {
  ref, computed, inject, onMounted,
} from '@vue/composition-api'
import {
  format, parseISO, isValid, startOfMonth, subMonths,
} from 'date-fns'

export const DATE_FORMAT = 'yyyy-MM-dd'

export const isValidDateFormat = (dateStr: string): boolean => {
  const date = parseISO(dateStr)

  return isValid(date) && format(date, DATE_FORMAT) === dateStr
}

export interface DateRange {
  startDate: string
  endDate: string
}

export function useDateRange(startDate?: string, endDate?: string) {
  const currentClubMeta = inject<{ today: string }>('currentClubMeta', { today: '' })

  const today = computed(() => currentClubMeta.today || format(new Date(), DATE_FORMAT))

  const dateRange = ref<string[]>([today.value, today.value])
  const startDateRef = computed(() => dateRange.value[0])
  const endDateRef = computed(() => dateRange.value[1])

  const displayRange = computed(() => {
    if (!startDateRef.value || !endDateRef.value) return ''

    return `${format(parseISO(startDateRef.value), 'yyyy/MM/dd')} ~ ${format(parseISO(endDateRef.value), 'yyyy/MM/dd')}`
  })

  const firstDayOfPreviousMonth = () => {
    // eslint-disable-next-line no-shadow
    const today = new Date()
    const targetMonth = subMonths(today, 1)

    return format(startOfMonth(targetMonth), 'yyyy-MM-dd')
  }

  const resetToDefault = () => {
    dateRange.value = [today.value, today.value]
  }

  const setDateRange = (incomingDateRange: string[]) => {
    const [incomingStartDate, incomingEndDate] = incomingDateRange
    if (incomingStartDate <= incomingEndDate) {
      dateRange.value = [
        isValidDateFormat(incomingStartDate) ? incomingStartDate : today.value,
        isValidDateFormat(incomingEndDate) ? incomingEndDate : today.value,
      ]
    } else {
      resetToDefault()
    }
  }

  onMounted(() => {
    if (startDate && endDate) {
      setDateRange([startDate, endDate])
    }
  })

  return {
    dateRange,
    displayRange,
    resetToDefault,
    setDateRange,
    isValidDateFormat,
    DATE_FORMAT,
    firstDayOfPreviousMonth,
    today,
  }
}
